import { Right } from '@talon/web/lib/api/models/Right';
import { Divider } from '@talon/web/src/components';
import { formatDateOnly } from '@talon/web/src/utils/dateOnly';
import { Box, Button, CheckBox, Text, Tip } from 'grommet';
import { Calendar, Down, Edit, Multimedia, Up } from 'grommet-icons';
import { TRightId } from 'lib/api';
import { useMemo, useState } from 'react';
import { borderRadiusSize, tooltipProps } from 'src/theme';
import { DocumentLink } from '../project-home/DocumentLink';
import { AdditionalInformation } from './AdditionalInformation';
import { BasicInformation } from './BasicInformation';
import { ContactInformation } from './ContactInformation';
import { RightStatusDisplay } from './RightStatusDisplay';
import { TermInformation } from './TermInformation';

export type RightPanelProps = {
  right: Partial<Right>;
  onEdit: (right: Partial<Right>) => Promise<void>;
  selected: boolean;
  setSelected: (rightId: TRightId, isSelected: boolean) => void;
  expanded?: boolean;
  rowIndex?: number;
};

export const RightPanel = ({
  right,
  onEdit,
  selected,
  setSelected,
  expanded = false,
}: RightPanelProps) => {
  /** State **/
  const [isExpanded, setIsExpanded] = useState(expanded);

  /** Computed **/
  const termFromDate = useMemo(() => formatDateOnly(right.termFromDate), [right]);
  const termToDate = useMemo(() => (right.termToDate ? formatDateOnly(right.termToDate) : 'Ongoing'), [right]);

  /** Render **/

  const Term = () => (
    <Box direction="row" pad="xsmall" gap="small" align="end">
      <Box height="1rem" justify="center">
        <Tip content="Term Dates">
          <Calendar size="1rem" />
        </Tip>
      </Box>
      <Box height="1rem" justify="center">
        <Text size="1rem" weight={400}>{`${termFromDate} - ${termToDate}`}</Text>
      </Box>
    </Box>
  );

  const MediaTypes = () => (
    <Box direction="row" pad="xsmall" gap="small" align="center">
      <Tip content="Media Types">
        <Multimedia size="1rem" />
      </Tip>
      <Text truncate size="1rem" weight={400}>
        {right.media}
      </Text>
    </Box>
  );

  return (
    <Box round={borderRadiusSize} background="light-2" elevation="xsmall">
      {/* Header */}
      <Box direction="row" align="center" gap="medium" pad={{ vertical: 'small', horizontal: 'medium' }}>
        <Box>
          <CheckBox
            checked={selected}
            onChange={() => {
              setSelected(right.id ?? '', !selected);
            }}
          />
        </Box>
        {/* `wrap` here is the actual fix: when the description (Title) is long, Info no longer
            has room on the same line and needs to be able to drop to one below it, rather than
            being forced to overflow past this row's edge and visually collide with the Buttons
            column that follows as its sibling. */}
        <Box
          direction="row"
          gap="medium"
          onClick={() => setIsExpanded(!isExpanded)}
          flex="grow"
          align="center"
          wrap
        >
          <Button
            icon={isExpanded ? <Up size="24px" /> : <Down size="24px" />}
            onClick={() => setIsExpanded(!isExpanded)}
            plain
            size="32px"
            tip={isExpanded ? 'Collapse details' : 'Expand details'}
          />

          {/* Title */}
          <Box gap="xxsmall">
            <Text weight="bold" size="medium">
              {right.description}
            </Text>
            <Divider />
            <Text size="small">{right.rightType}</Text>
          </Box>

          {/* Info */}
          <Box direction="row" align="center" justify="around" gap="medium" wrap>
            <Term />
            <MediaTypes />
            <RightStatusDisplay right={right} />
          </Box>
        </Box>

        {/* Buttons */}
        <Box direction="row" gap="medium" justify="end">
          <Box width="22px">{right.document && <DocumentLink size="22px" doc={right.document} />}</Box>
          <Button
            icon={<Edit size="22px" />}
            tip={{
              content: 'Edit Right',
              dropProps: tooltipProps,
              plain: true,
            }}
            onClick={() => onEdit(right)}
            plain
            hoverIndicator
          />
        </Box>
      </Box>

      {/* Expanded Details */}
      {isExpanded && (
        <Box
          pad={{ horizontal: 'small', top: 'xsmall', bottom: 'small' }}
          round={{
            corner: 'bottom',
            size: borderRadiusSize,
          }}
          justify="between"
          gap="small"
          fill="horizontal"
          background="light-2"
        >
          <BasicInformation right={right} />
          <TermInformation right={right} />
          <ContactInformation right={right} />

          {(right.restrictionsOnUse || right.notes) && (
            <AdditionalInformation restrictionsOnUse={right.restrictionsOnUse} notes={right.notes} />
          )}
        </Box>
      )}
    </Box>
  );
};
